import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Mail, Plus, Play, Pause, Trash2, Edit, Settings, Users, Clock, 
  CheckCircle, Send, Eye, MousePointer, Loader2, ChevronRight, Server,
  RefreshCw, BarChart3, Zap, AlertTriangle
} from 'lucide-react';
import api from '../api';

const EmailSequencesPage = () => {
  const [activeTab, setActiveTab] = useState('sequences');
  const [loading, setLoading] = useState(true);
  const [sequences, setSequences] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [smtpConfigs, setSmtpConfigs] = useState([]);
  const [showSequenceDialog, setShowSequenceDialog] = useState(false);
  const [showSmtpDialog, setShowSmtpDialog] = useState(false);
  const [selectedSequence, setSelectedSequence] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  
  // New sequence form
  const [newSequence, setNewSequence] = useState({
    name: '',
    description: '',
    trigger_type: 'manual',
    steps: []
  });

  // New SMTP form
  const [newSmtp, setNewSmtp] = useState({
    name: 'Outlook SMTP',
    host: 'smtp.office365.com',
    port: 587,
    username: '',
    password: '',
    use_tls: true,
    from_name: '',
    from_email: '',
    is_default: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [seqRes, dashRes, smtpRes] = await Promise.all([
        api.get('/api/email/sequences'),
        api.get('/api/email/sequences/dashboard'),
        api.get('/api/email/smtp-config').catch(() => ({ data: null }))
      ]);
      setSequences(seqRes.data.sequences || []);
      setDashboard(dashRes.data);
      if (smtpRes.data) {
        setSmtpConfigs([smtpRes.data]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createSequence = async () => {
    try {
      await api.post('/api/email/sequences', newSequence);
      setShowSequenceDialog(false);
      setNewSequence({
        name: '',
        description: '',
        trigger_type: 'manual',
        steps: []
      });
      loadData();
    } catch (error) {
      console.error('Error creating sequence:', error);
    }
  };

  const activateSequence = async (id) => {
    try {
      await api.put(`/api/email/sequences/${id}`, { status: 'active' });
      loadData();
    } catch (error) {
      console.error('Error activating sequence:', error);
    }
  };

  const pauseSequence = async (id) => {
    try {
      await api.put(`/api/email/sequences/${id}`, { status: 'paused' });
      loadData();
    } catch (error) {
      console.error('Error pausing sequence:', error);
    }
  };

  const deleteSequence = async (id) => {
    if (!window.confirm('Are you sure you want to delete this sequence?')) return;
    try {
      await api.delete(`/api/email/sequences/${id}`);
      loadData();
    } catch (error) {
      console.error('Error deleting sequence:', error);
    }
  };

  const saveSmtpConfig = async () => {
    try {
      await api.post('/api/email/smtp-config', newSmtp);
      setShowSmtpDialog(false);
      loadData();
    } catch (error) {
      console.error('Error saving SMTP config:', error);
    }
  };

  const testSmtpConfig = async () => {
    const testEmail = prompt('Enter email address to send test to:');
    if (!testEmail) return;
    try {
      const res = await api.post(`/api/email/smtp-config/test?test_email=${testEmail}`);
      alert(res.data.message || 'Test email sent!');
    } catch (error) {
      alert('Test failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-slate-100 text-slate-700',
      active: 'bg-emerald-100 text-emerald-700',
      paused: 'bg-amber-100 text-amber-700',
      completed: 'bg-blue-100 text-blue-700',
      archived: 'bg-slate-100 text-slate-500'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const filteredSequences = sequences.filter(seq => 
    statusFilter === 'all' || !statusFilter || seq.status === statusFilter
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const stats = dashboard || {};

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Email Sequences</h1>
          <p className="text-slate-500 mt-1">Automated multi-step email campaigns</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowSmtpDialog(true)}>
            <Server className="h-4 w-4 mr-2" />
            SMTP Settings
          </Button>
          <Button onClick={() => setShowSequenceDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Sequence
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Sequences</p>
                <p className="text-2xl font-bold">{stats.sequences?.total || 0}</p>
              </div>
              <Mail className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Active</p>
                <p className="text-2xl font-bold text-emerald-600">{stats.sequences?.active || 0}</p>
              </div>
              <Play className="h-8 w-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Emails Sent</p>
                <p className="text-2xl font-bold">{stats.emails?.sent || 0}</p>
              </div>
              <Send className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Open Rate</p>
                <p className="text-2xl font-bold">{stats.emails?.open_rate || 0}%</p>
              </div>
              <Eye className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Click Rate</p>
                <p className="text-2xl font-bold">{stats.emails?.click_rate || 0}%</p>
              </div>
              <MousePointer className="h-8 w-8 text-cyan-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* SMTP Status Banner */}
      {smtpConfigs.length === 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <div className="flex-1">
                <p className="font-medium text-amber-800">SMTP Not Configured</p>
                <p className="text-sm text-amber-700">
                  Configure your Outlook/SMTP settings to send real emails. Currently using mock mode.
                </p>
              </div>
              <Button variant="outline" onClick={() => setShowSmtpDialog(true)}>
                Configure SMTP
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="sequences">
            <Mail className="h-4 w-4 mr-2" />
            Sequences
          </TabsTrigger>
          <TabsTrigger value="enrollments">
            <Users className="h-4 w-4 mr-2" />
            Enrollments
          </TabsTrigger>
          <TabsTrigger value="analytics">
            <BarChart3 className="h-4 w-4 mr-2" />
            Analytics
          </TabsTrigger>
        </TabsList>

        {/* Sequences Tab */}
        <TabsContent value="sequences" className="space-y-4">
          {/* Filter */}
          <div className="flex items-center gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={loadData}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          {/* Sequences List */}
          {filteredSequences.length > 0 ? (
            <div className="space-y-3">
              {filteredSequences.map((seq) => (
                <Card key={seq.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="py-4">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Mail className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900">{seq.name}</h3>
                          <Badge className={getStatusBadge(seq.status)}>{seq.status}</Badge>
                        </div>
                        <p className="text-sm text-slate-500">{seq.description || 'No description'}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            <Zap className="h-3 w-3" />
                            {seq.steps?.length || 0} steps
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {seq.enrolled_count || 0} enrolled
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Trigger: {seq.trigger_type}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {seq.status === 'draft' && (
                          <Button size="sm" onClick={() => activateSequence(seq.id)}>
                            <Play className="h-4 w-4 mr-1" />
                            Activate
                          </Button>
                        )}
                        {seq.status === 'active' && (
                          <Button size="sm" variant="outline" onClick={() => pauseSequence(seq.id)}>
                            <Pause className="h-4 w-4 mr-1" />
                            Pause
                          </Button>
                        )}
                        {seq.status === 'paused' && (
                          <Button size="sm" onClick={() => activateSequence(seq.id)}>
                            <Play className="h-4 w-4 mr-1" />
                            Resume
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => setSelectedSequence(seq)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-red-600 hover:text-red-700"
                          onClick={() => deleteSequence(seq.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Mail className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <h3 className="font-medium text-slate-900 mb-2">No Email Sequences</h3>
                <p className="text-slate-500 mb-4">Create your first automated email sequence</p>
                <Button onClick={() => setShowSequenceDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Sequence
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Enrollments Tab */}
        <TabsContent value="enrollments">
          <Card>
            <CardContent className="py-8 text-center">
              <Users className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <h3 className="font-medium text-slate-900 mb-2">Active Enrollments</h3>
              <p className="text-slate-500">
                {stats.enrollments?.active || 0} contacts currently in sequences
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Performance Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Total Enrolled</span>
                    <span className="font-semibold">{stats.enrollments?.total || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Completed</span>
                    <span className="font-semibold text-emerald-600">{stats.enrollments?.completed || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Emails Opened</span>
                    <span className="font-semibold">{stats.emails?.opened || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Links Clicked</span>
                    <span className="font-semibold">{stats.emails?.clicked || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(stats.recent_sequences || []).map((seq, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{seq.name}</p>
                        <p className="text-xs text-slate-500">{seq.step_count} steps</p>
                      </div>
                      <Badge className={getStatusBadge(seq.status)}>{seq.status}</Badge>
                    </div>
                  ))}
                  {(!stats.recent_sequences || stats.recent_sequences.length === 0) && (
                    <p className="text-slate-500 text-sm">No recent activity</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Sequence Dialog */}
      <Dialog open={showSequenceDialog} onOpenChange={setShowSequenceDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Email Sequence</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Sequence Name</Label>
              <Input
                value={newSequence.name}
                onChange={(e) => setNewSequence({ ...newSequence, name: e.target.value })}
                placeholder="e.g., Welcome Series"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newSequence.description}
                onChange={(e) => setNewSequence({ ...newSequence, description: e.target.value })}
                placeholder="Describe what this sequence does..."
                rows={2}
              />
            </div>
            <div>
              <Label>Trigger Type</Label>
              <Select
                value={newSequence.trigger_type}
                onValueChange={(v) => setNewSequence({ ...newSequence, trigger_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual Enrollment</SelectItem>
                  <SelectItem value="lead_source">New Lead from Source</SelectItem>
                  <SelectItem value="lead_status_change">Lead Status Change</SelectItem>
                  <SelectItem value="tag_added">Tag Added</SelectItem>
                  <SelectItem value="form_submission">Form Submission</SelectItem>
                  <SelectItem value="deal_stage_change">Deal Stage Change</SelectItem>
                  <SelectItem value="cold_call_converted">Cold Call Converted</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSequenceDialog(false)}>Cancel</Button>
            <Button onClick={createSequence} disabled={!newSequence.name}>
              Create Sequence
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SMTP Configuration Dialog */}
      <Dialog open={showSmtpDialog} onOpenChange={setShowSmtpDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>SMTP Configuration</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
              <strong>Outlook/Office 365:</strong> Use smtp.office365.com with port 587
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>SMTP Host</Label>
                <Input
                  value={newSmtp.host}
                  onChange={(e) => setNewSmtp({ ...newSmtp, host: e.target.value })}
                  placeholder="smtp.office365.com"
                />
              </div>
              <div>
                <Label>Port</Label>
                <Input
                  type="number"
                  value={newSmtp.port}
                  onChange={(e) => setNewSmtp({ ...newSmtp, port: parseInt(e.target.value) })}
                  placeholder="587"
                />
              </div>
            </div>
            <div>
              <Label>Username (Email)</Label>
              <Input
                value={newSmtp.username}
                onChange={(e) => setNewSmtp({ ...newSmtp, username: e.target.value })}
                placeholder="your.email@company.com"
              />
            </div>
            <div>
              <Label>Password / App Password</Label>
              <Input
                type="password"
                value={newSmtp.password}
                onChange={(e) => setNewSmtp({ ...newSmtp, password: e.target.value })}
                placeholder="Your email password or app password"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>From Name</Label>
                <Input
                  value={newSmtp.from_name}
                  onChange={(e) => setNewSmtp({ ...newSmtp, from_name: e.target.value })}
                  placeholder="Sales Team"
                />
              </div>
              <div>
                <Label>From Email</Label>
                <Input
                  value={newSmtp.from_email}
                  onChange={(e) => setNewSmtp({ ...newSmtp, from_email: e.target.value })}
                  placeholder="Same as username if blank"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={newSmtp.use_tls}
                onCheckedChange={(v) => setNewSmtp({ ...newSmtp, use_tls: v })}
              />
              <Label>Use TLS (recommended)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={testSmtpConfig}>
              Test Connection
            </Button>
            <Button onClick={saveSmtpConfig}>
              Save Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailSequencesPage;
